import * as THREE from 'three'

/**
 * Afterburner fire effect off the back of the skis when going over 100mph.
 * Two flame trails (one per ski) with orange/red/yellow particles.
 */

const MAX_PARTICLES = 120
const AFTERBURNER_THRESHOLD = 44.7  // m/s = ~100 mph

interface FlameParticle {
  mesh: THREE.Mesh
  velocity: THREE.Vector3
  life: number
  maxLife: number
}

export class Afterburner {
  private scene: THREE.Scene
  private particles: FlameParticle[] = []
  private pool: THREE.Mesh[] = []
  private spawnTimer = 0
  private flameColors = [0xff0000, 0xdd0000, 0xff1100, 0xcc0000, 0xff2200, 0xee1100]

  constructor(scene: THREE.Scene) {
    this.scene = scene

    const geo = new THREE.SphereGeometry(0.12, 4, 4)
    for (let i = 0; i < MAX_PARTICLES; i++) {
      const mat = new THREE.MeshBasicMaterial({
        color: 0xff6600,
        transparent: true,
        opacity: 0.9,
      })
      const mesh = new THREE.Mesh(geo, mat)
      mesh.visible = false
      scene.add(mesh)
      this.pool.push(mesh)
    }
  }

  update(
    dt: number,
    skierX: number,
    skierY: number,
    skierZ: number,
    speed: number,
    isAirborne: boolean,
  ): void {
    const isActive = speed >= AFTERBURNER_THRESHOLD

    // Spawn flames when over 100mph
    this.spawnTimer -= dt
    if (isActive && this.spawnTimer <= 0) {
      this.spawnTimer = 0.01  // very fast spawn rate for thick flames

      // Intensity scales with speed over threshold
      const intensity = Math.min((speed - AFTERBURNER_THRESHOLD) / 20, 1)
      const count = Math.floor(2 + intensity * 4)

      for (let i = 0; i < count; i++) {
        // Spawn from each ski
        for (const skiOffset of [-0.15, 0.15]) {
          this.spawnFlame(skierX + skiOffset, skierY, skierZ, speed, intensity)
        }
      }
    }

    // Update particles
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i]
      p.life -= dt

      if (p.life <= 0) {
        p.mesh.visible = false
        this.particles.splice(i, 1)
        continue
      }

      p.mesh.position.add(p.velocity.clone().multiplyScalar(dt))

      // Flames rise slightly and spread
      p.velocity.y += 2 * dt
      p.velocity.x += (Math.random() - 0.5) * 3 * dt

      // Fade and shrink
      const t = p.life / p.maxLife
      const mat = p.mesh.material as THREE.MeshBasicMaterial
      mat.opacity = t * 0.9

      // Color shift: bright red -> dark red as particle ages
      if (t > 0.6) {
        mat.color.setHex(0xff2200)  // bright red (fresh)
      } else if (t > 0.3) {
        mat.color.setHex(0xdd0000)  // red
      } else {
        mat.color.setHex(0x880000)  // dark red (dying)
      }

      const scale = (0.3 + t * 0.7) * (1 + (1 - t) * 0.5)
      p.mesh.scale.setScalar(scale)
    }
  }

  private spawnFlame(x: number, y: number, z: number, speed: number, intensity: number): void {
    const mesh = this.pool.find(m => !m.visible)
    if (!mesh) return

    mesh.visible = true
    mesh.position.set(
      x + (Math.random() - 0.5) * 0.15,
      y + 0.15 + Math.random() * 0.2,
      z - 0.3 - Math.random() * 0.5  // behind the skier (uphill, visible to camera)
    )

    const mat = mesh.material as THREE.MeshBasicMaterial
    mat.color.setHex(this.flameColors[Math.floor(Math.random() * this.flameColors.length)])
    mat.opacity = 0.95

    mesh.scale.setScalar(0.8 + intensity * 0.8)

    const life = 0.3 + Math.random() * 0.3

    this.particles.push({
      mesh,
      velocity: new THREE.Vector3(
        (Math.random() - 0.5) * 2,
        1 + Math.random() * 2,
        -4 - Math.random() * 5  // strong trail behind (toward camera)
      ),
      life,
      maxLife: life,
    })
  }
}
